import React, { memo, useMemo, useRef, useState } from 'react';

import { Center, Text } from '@chakra-ui/react';

import { useQuery } from 'react-query';

import { washHub } from 'store/slices';
import { useWashHubListener } from 'hooks';
import { fetchPickups } from 'api/pickup';
import { Pickup } from 'types/pickup';
import { Status } from 'types/items';
import { DataTable, ServerSort } from 'components/modules/DataTable';
import {
  EditPickupModal,
  EditPickupModalRef,
} from 'components/modules/EditPickupModal';

import { PickupNavButton } from './PickupNavButton';
import { buildPickupColumns } from './pickupsColumns';

export type PickupsTableProps = {
  /** Element used in the react-query key for this tab ('all' or a Status). */
  statusKey: string | Status;
  status?: Status | Status[];
  startDate?: Date;
  endDate?: Date;
  venue?: string[];
  collectionPoint?: string[];
};

/**
 * Shared body of the four Pickups tabs — the query, sort state, table, and
 * EditPickupModal wiring were copy-pasted per tab and differed only in the
 * status filter, which is a prop here.
 */
export const PickupsTable = memo<PickupsTableProps>((props) => {
  const {
    statusKey,
    status,
    endDate,
    startDate,
    venue,
    collectionPoint,
  } = props;

  const [page, setPage] = useState<number>(1);
  const [sort, setSort] = useState<ServerSort | undefined>(undefined);

  const editPickupModalRef = useRef<EditPickupModalRef>(null);
  const washHubId = washHub.selectors.useSelectedWashHubId();
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();

  const queryKey = [
    `fetch-pickups-${washHubId}`,
    page,
    statusKey,
    endDate,
    startDate,
    venue?.toString(),
    collectionPoint?.toString(),
    sort?.field,
    sort?.dir,
  ];

  const queryFn = () =>
    fetchPickups({
      page,
      limit: 10,
      washHub: washHubId,
      endDate,
      startDate,
      ...(status !== undefined ? { status } : {}),
      venue,
      collectionPoint,
      sortByField: sort?.field,
      sort: sort?.dir,
    });

  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
  };

  const { data, isLoading, isFetching } = useQuery(queryKey, queryFn, options);

  useWashHubListener(() => {
    setPage(1);
  });

  const columns = useMemo(() => buildPickupColumns(), []);

  const pickups = data?.items || [];

  const pagination = {
    page,
    setPage,
    totalCount: data?.total || 0,
    totalPages: data?.totalPages || 0,
  };

  return (
    <>
      <DataTable<Pickup>
        minW="1000px"
        data={pickups}
        columns={columns}
        isLoading={isLoading || isFetching}
        pagination={pagination}
        sort={sort}
        onSortChange={setSort}
        onRowClick={(pickup) => {
          editPickupModalRef?.current?.onOpen(pickup);
        }}
        renderEmpty={() => (
          <Center p={8}>
            <Text fontSize="20px">No Pickups available</Text>
          </Center>
        )}
      />
      <EditPickupModal
        ref={editPickupModalRef}
        renderNextPickupButton={(currentPickup) => {
          const currentIndex = pickups.findIndex(
            (o) => o._id === currentPickup._id,
          );

          if (currentIndex === pickups.length - 1) {
            return null;
          }

          return (
            <PickupNavButton
              type="right"
              onClick={() => {
                const nextOrder = pickups[currentIndex + 1];

                editPickupModalRef.current?.onOpen(nextOrder);
              }}
            />
          );
        }}
        renderPreviousPickupButton={(currentPickup) => {
          const currentIndex = pickups.findIndex(
            (o) => o._id === currentPickup._id,
          );

          if (currentIndex === 0) {
            return null;
          }

          return (
            <PickupNavButton
              type="left"
              onClick={() => {
                const previousOrder = pickups[currentIndex - 1];

                editPickupModalRef.current?.onOpen(previousOrder);
              }}
            />
          );
        }}
      />
    </>
  );
});
